// ============================================================================
// AUTH GUARD UTILITIES
// ============================================================================
// Centralised helpers for performing a *complete* logout:
//   1. Clear every piece of client-side session state (localStorage + sessionStorage)
//   2. Broadcast the logout event so every open tab of the app signs out too
//   3. Prevent the user from navigating back into authenticated pages via the
//      browser back button (or a BFCache-restored page)

const LOGOUT_STORAGE_KEY = '__lmms_logout_event';
const LOGIN_STORAGE_KEY = '__lmms_login_event';
const LOGOUT_CHANNEL_NAME = 'lmms-auth-channel';
const TAB_HEARTBEAT_KEY = '__lmms_tab_heartbeat';
const HEARTBEAT_INTERVAL_MS = 2000;
const HEARTBEAT_FRESH_MS = 5000;

let broadcastChannel: BroadcastChannel | null = null;

const getBroadcastChannel = (): BroadcastChannel | null => {
  if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') {
    return null;
  }
  if (!broadcastChannel) {
    broadcastChannel = new BroadcastChannel(LOGOUT_CHANNEL_NAME);
  }
  return broadcastChannel;
};

/**
 * Wipe every key from both localStorage and sessionStorage.
 * Any partial cleanup risks leaving stale user context (permissions, branch,
 * cached user object) that the app would then read on the next login.
 */
export const clearAllAuthStorage = (): void => {
  try {
    localStorage.clear();
  } catch {
    /* storage may be unavailable in some contexts (private mode quotas, etc.) */
  }
  try {
    sessionStorage.clear();
  } catch {
    /* ignore */
  }
};

/**
 * Notify every other tab of this app that a logout has occurred.
 * Uses BroadcastChannel where available, with a `storage` event fallback so
 * older browsers (and cross-origin iframes) still receive the signal.
 */
export const broadcastLogout = (): void => {
  const channel = getBroadcastChannel();
  if (channel) {
    try {
      channel.postMessage({ type: 'logout', ts: Date.now() });
    } catch {
      /* ignore */
    }
  }
  try {
    // Fire a storage event by writing + removing a sentinel key.
    // Other tabs receive `window` storage events (they don't fire in the
    // originating tab, which is exactly what we want).
    localStorage.setItem(LOGOUT_STORAGE_KEY, String(Date.now()));
    localStorage.removeItem(LOGOUT_STORAGE_KEY);
  } catch {
    /* ignore */
  }
};

/**
 * Subscribe to logout broadcasts from other tabs.
 * Returns an unsubscribe function.
 */
export const initCrossTabLogout = (onLogout: () => void): (() => void) => {
  const handleStorage = (event: StorageEvent) => {
    if (event.key === LOGOUT_STORAGE_KEY && event.newValue) {
      onLogout();
    }
  };
  window.addEventListener('storage', handleStorage);

  const channel = getBroadcastChannel();
  const handleMessage = (event: MessageEvent) => {
    if (event?.data?.type === 'logout') {
      onLogout();
    }
  };
  channel?.addEventListener('message', handleMessage);

  return () => {
    window.removeEventListener('storage', handleStorage);
    channel?.removeEventListener('message', handleMessage);
  };
};

/**
 * Notify every other tab of this app that a login has occurred.
 * Mirror of broadcastLogout — same dual-channel (BroadcastChannel +
 * storage event) strategy so sibling tabs sitting on the login page can
 * hop straight to the dashboard without waiting for a refresh.
 */
export const broadcastLogin = (): void => {
  const channel = getBroadcastChannel();
  if (channel) {
    try {
      channel.postMessage({ type: 'login', ts: Date.now() });
    } catch {
      /* ignore */
    }
  }
  try {
    localStorage.setItem(LOGIN_STORAGE_KEY, String(Date.now()));
    localStorage.removeItem(LOGIN_STORAGE_KEY);
  } catch {
    /* ignore */
  }
};

/**
 * Subscribe to login broadcasts from other tabs.
 * Returns an unsubscribe function.
 */
export const initCrossTabLogin = (onLogin: () => void): (() => void) => {
  const handleStorage = (event: StorageEvent) => {
    if (event.key === LOGIN_STORAGE_KEY && event.newValue) {
      onLogin();
    }
  };
  window.addEventListener('storage', handleStorage);

  const channel = getBroadcastChannel();
  const handleMessage = (event: MessageEvent) => {
    if (event?.data?.type === 'login') {
      onLogin();
    }
  };
  channel?.addEventListener('message', handleMessage);

  return () => {
    window.removeEventListener('storage', handleStorage);
    channel?.removeEventListener('message', handleMessage);
  };
};

// ============================================================================
// SINGLE-TAB PRESENCE PROTOCOL
// ============================================================================
// Enforces "one tab per browser profile" for authenticated sessions.
// Every tab does two things:
//   1. Writes a heartbeat to localStorage every HEARTBEAT_INTERVAL_MS. A tab
//      is considered "alive" if its heartbeat is fresher than HEARTBEAT_FRESH_MS.
//   2. Talks over BroadcastChannel: pings on mount, pongs on ping, and can
//      broadcast a takeover when the user chooses to "continue here" — which
//      locks out every other tab.
// The heartbeat is the fallback for browsers without BroadcastChannel and also
// handles the case where the "existing" tab is already there before this one
// even boots (BroadcastChannel ping/pong only helps if both tabs are running
// simultaneously — the heartbeat catches the case where the other tab was
// there first).

const generateTabId = (): string => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through to fallback */
  }
  return `tab-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

interface HeartbeatRecord {
  tabId: string;
  ts: number;
}

const readHeartbeat = (): HeartbeatRecord | null => {
  try {
    const raw = localStorage.getItem(TAB_HEARTBEAT_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as HeartbeatRecord;
  } catch {
    return null;
  }
};

const writeHeartbeat = (tabId: string): void => {
  try {
    localStorage.setItem(
      TAB_HEARTBEAT_KEY,
      JSON.stringify({ tabId, ts: Date.now() } satisfies HeartbeatRecord),
    );
  } catch {
    /* ignore */
  }
};

const clearOwnHeartbeat = (tabId: string): void => {
  const current = readHeartbeat();
  if (current?.tabId === tabId) {
    try {
      localStorage.removeItem(TAB_HEARTBEAT_KEY);
    } catch {
      /* ignore */
    }
  }
};

const isForeignHeartbeatFresh = (myTabId: string): boolean => {
  const current = readHeartbeat();
  if (!current || current.tabId === myTabId) return false;
  return Date.now() - current.ts < HEARTBEAT_FRESH_MS;
};

interface PresenceHandlers {
  /** Called if another tab of this app is already alive when we mount. */
  onExistingTabDetected: () => void;
  /** Called if another tab takes over — this tab must lock itself out. */
  onTakeoverReceived: () => void;
}

/**
 * Start the single-tab presence protocol for this tab.
 * Returns a stable `tabId`, a `takeOver()` function that boots every other
 * tab, and a `stop()` teardown function.
 */
export const initTabPresence = (
  handlers: PresenceHandlers,
): { tabId: string; takeOver: () => void; stop: () => void } => {
  const tabId = generateTabId();
  const channel = getBroadcastChannel();

  // Step 1 — heartbeat-based check: is another tab already alive?
  if (isForeignHeartbeatFresh(tabId)) {
    handlers.onExistingTabDetected();
  }

  // Step 2 — broadcast a ping so any live tab responds with a pong.
  const handleMessage = (event: MessageEvent) => {
    const data = event?.data;
    if (!data || typeof data !== 'object' || data.tabId === tabId) return;

    if (data.type === 'presence-ping') {
      channel?.postMessage({ type: 'presence-pong', tabId });
    } else if (data.type === 'presence-pong') {
      handlers.onExistingTabDetected();
    } else if (data.type === 'presence-takeover') {
      handlers.onTakeoverReceived();
    }
  };
  channel?.addEventListener('message', handleMessage);

  try {
    channel?.postMessage({ type: 'presence-ping', tabId });
  } catch {
    /* ignore */
  }

  // Step 3 — start heartbeat.
  writeHeartbeat(tabId);
  const interval = window.setInterval(() => writeHeartbeat(tabId), HEARTBEAT_INTERVAL_MS);

  // Step 4 — clear our heartbeat on unload so a reload doesn't self-lock.
  const handleUnload = () => clearOwnHeartbeat(tabId);
  window.addEventListener('pagehide', handleUnload);
  window.addEventListener('beforeunload', handleUnload);

  const takeOver = () => {
    try {
      channel?.postMessage({ type: 'presence-takeover', tabId });
    } catch {
      /* ignore */
    }
    // Reclaim heartbeat immediately so the old tab's next read sees ours.
    writeHeartbeat(tabId);
  };

  const stop = () => {
    channel?.removeEventListener('message', handleMessage);
    window.removeEventListener('pagehide', handleUnload);
    window.removeEventListener('beforeunload', handleUnload);
    window.clearInterval(interval);
    clearOwnHeartbeat(tabId);
  };

  return { tabId, takeOver, stop };
};

/**
 * Trap the browser back button on the current URL. Every time the user tries
 * to pop back into an authenticated page, we re-push the current entry so the
 * URL stays put. Also guards against Back/Forward Cache restoring a stale
 * authenticated page — if that happens we force a full reload.
 *
 * Intended to be installed once from the login route (post-logout landing).
 */
export const installBackNavigationGuard = (): (() => void) => {
  const push = () => {
    try {
      window.history.pushState(null, '', window.location.href);
    } catch {
      /* ignore */
    }
  };

  push();

  const onPopState = () => push();
  const onPageShow = (event: PageTransitionEvent) => {
    if (event.persisted) {
      // Page restored from BFCache while unauthenticated — do a hard reload
      // so any cached rendered UI is discarded.
      window.location.reload();
    }
  };

  window.addEventListener('popstate', onPopState);
  window.addEventListener('pageshow', onPageShow);

  return () => {
    window.removeEventListener('popstate', onPopState);
    window.removeEventListener('pageshow', onPageShow);
  };
};

/**
 * Perform a complete logout:
 *   1. Best-effort server call (revoke refresh token) — errors are swallowed
 *      so the client-side cleanup always runs
 *   2. Broadcast to sibling tabs
 *   3. Clear all client storage
 *   4. `location.replace('/')` so the current entry is overwritten (back
 *      button won't return to an authenticated page)
 */
export const performLogout = async (
  serverLogout?: () => Promise<unknown>,
): Promise<void> => {
  if (serverLogout) {
    try {
      await serverLogout();
    } catch {
      /* backend call is best-effort — proceed with local cleanup regardless */
    }
  }

  broadcastLogout();
  clearAllAuthStorage();

  window.location.replace('/');
};
